// Shapes shared by the server and the client. Dates are local calendar days as
// YYYY-MM-DD strings: the client decides what "today" is (the person travels),
// and Strava activities carry their own local start date.

export interface User {
  id: string;
  email: string;
  name: string;
}

export interface Me {
  user: User;
  ai: boolean;
}

export const MEALS = ['breakfast', 'lunch', 'dinner', 'snack'] as const;
export type Meal = (typeof MEALS)[number];

export interface Macros {
  kcal: number;
  protein: number;
  carbs: number;
  fat: number;
  fiber: number;
}

export type EntrySource = 'manual' | 'food' | 'ai' | 'off';

export interface Entry extends Macros {
  id: string;
  date: string;
  meal: Meal;
  name: string;
  /** Free text: "2 eggs", "1.5 servings (45 g)". */
  amount: string;
  /** The saved food this came from, so the entry can be re-logged or rescaled. */
  foodId: string | null;
  /** How many of the food's servings this entry is; null when not from a food. */
  servings: number | null;
  source: EntrySource;
  createdAt: number;
}

/** A saved food. Macros are per one serving. */
export interface Food extends Macros {
  id: string;
  name: string;
  brand: string;
  serving: string;
  servingGrams: number | null;
  barcode: string | null;
  source: 'custom' | 'ai' | 'off';
  useCount: number;
  lastUsed: number;
}

export type ActivitySource = 'strava' | 'trainer';

/** A Strava activity as stored: raw facts only. Calorie estimates are derived. */
export interface Activity {
  id: string;
  source: ActivitySource;
  date: string;
  start: number;
  sport: string;
  name: string;
  movingS: number;
  distanceM: number | null;
  avgHr: number | null;
  kj: number | null;
  /** Strava's own figure, only present on detailed activities (direct sync). */
  stravaKcal: number | null;
}

export type BurnMethod = 'strava' | 'power' | 'hr' | 'distance' | 'met';

export interface ActivityBurn extends Activity {
  /** Calories above resting for the activity's duration. */
  kcal: number;
  method: BurnMethod;
  /** Set when this overlaps a better-measured recording of the same workout; not counted. */
  duplicateOf: string | null;
}

export interface Weight {
  date: string;
  kg: number;
}

export interface Settings {
  sex: 'male' | 'female';
  birthYear: number;
  heightCm: number;
  units: 'metric' | 'imperial';
  /** Signed: negative to lose. */
  rateKgPerWeek: number;
  proteinGPerKg: number;
  fatPct: number;
  /** Daily targets follow the day's Strava activity rather than a flat average. */
  exerciseAdjust: boolean;
  /** Used until the first weigh-in. */
  startWeightKg: number;
}

export const DEFAULT_SETTINGS: Settings = {
  sex: 'male',
  birthYear: 1990,
  heightCm: 178,
  units: 'metric',
  rateKgPerWeek: 0,
  proteinGPerKg: 2,
  fatPct: 0.3,
  exerciseAdjust: true,
  startWeightKg: 75,
};

export interface Targets extends Macros {
  baseline: number;
  exercise: number;
  expenditure: number;
}

export interface DayPoint {
  date: string;
  intake: number | null;
  exercise: number;
  /** Non-exercise expenditure estimate (BMR, daily movement, digestion). */
  baseline: number;
  expenditure: number;
  scaleKg: number | null;
  trendKg: number | null;
}

export interface DaySummary {
  date: string;
  entries: Entry[];
  activities: ActivityBurn[];
  weight: Weight | null;
  targets: Targets;
  totals: Macros;
  trendKg: number;
}

export interface Insights {
  days: DayPoint[];
  /** Days of logged intake inside the current estimation window. */
  loggedDays: number;
  /** 0..1: how much the current estimate rests on your own data vs the formula. */
  confidence: number;
  expenditure: number;
  baseline: number;
  avgExercise: number;
  trendKg: number;
  weeklyChangeKg: number | null;
}

export interface StravaStatus {
  source: ActivitySource | null;
  configured: boolean;
  connected: boolean;
  athleteName: string | null;
  lastSync: number | null;
  lastError: string | null;
}

export interface AiLogResult {
  entries: Entry[];
  note: string;
}

export interface FoodHit extends Macros {
  name: string;
  brand: string;
  serving: string;
  servingGrams: number | null;
  barcode: string | null;
  /** Macros per 100 g when known, so the amount can be changed. */
  per100g: Macros | null;
}
